/**
 * The two ways the Athletes list reads a roster, both sized for a real gym (#2669).
 *
 * The list used to collect every athlete_id first and paste them into an
 * `id=in.(…)` filter. At 213 athletes that URL reached 8,505 chars and the
 * gateway answered `414 Request-URI Too Large` — so the staff Athletes list
 * failed for exactly the gyms with the biggest rosters, and never for the small
 * ones anybody tests with.
 */

import { supabase } from '@/utils/supabase/client';
import { fetchAllPages } from '@/utils/supabase/fetchAllPages';
import { fetchInChunks } from '@/utils/supabase/fetchInChunks';

export interface RosterReadResult {
  data: any[];
  error: Error | null;
}

const ROSTER_SELECT = '*, athlete_skill_levels(tumbling_level, stunting_positions, prior_experience, special_skills)';

/**
 * Whole-gym roster, filtered server-side through the membership join so no id
 * list ever travels in the URL — and one fewer round trip than the two-step
 * shape it replaces. The `athlete_organizations_athlete_id_fkey` FK is what
 * makes the `!inner` embed resolvable; PostgREST embeds need a real FK.
 *
 * Paged because PostgREST caps an unpaginated select at 1,000 rows
 * (`max_rows` in supabase/config.toml), which would silently truncate a very
 * large gym's roster rather than fail visibly.
 */
export async function fetchOrganizationRoster(organizationId: string): Promise<RosterReadResult> {
  const { rows, error } = await fetchAllPages<any>((from, to) =>
    supabase
      .from('athletes')
      .select(`${ROSTER_SELECT}, athlete_organizations!inner(organization_id)`)
      .eq('athlete_organizations.organization_id', organizationId)
      .eq('is_draft', false)
      .order('id', { ascending: true })
      .range(from, to)
  );
  return { data: rows, error };
}

/**
 * Own-family roster. These ids are computed client-side (parent relationships
 * intersected with this gym's non-independent athletes), so they cannot be
 * expressed as a server-side filter and must be chunked to stay under the URL
 * limit — a guardian on a large blended family account can hold far more
 * athletes than the two or three a developer pictures.
 */
export async function fetchRosterByAthleteIds(athleteIds: string[]): Promise<RosterReadResult> {
  const { rows, error } = await fetchInChunks<any>(athleteIds, (chunk) =>
    fetchAllPages<any>((from, to) =>
      supabase
        .from('athletes')
        .select(ROSTER_SELECT)
        .in('id', chunk)
        .eq('is_draft', false)
        .order('id', { ascending: true })
        .range(from, to)
    ).then(({ rows: pageRows, error: pageError }) => ({
      data: pageRows,
      error: pageError ? { message: pageError.message } : null,
    }))
  );
  return { data: rows, error };
}
